import React, {useCallback, useContext, useMemo} from 'react';

import {NumberInput, NumberInputTypes} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark/required-mark';
import {ValidatedControl} from 'src/contexts/validation-context';
import {MAX_CONTRACT_FIELD_VALUE} from 'src/modules/profile/account/constants';
import {getCost} from 'src/modules/profile/account/tabs/admin-account-api-v2/utils';

import Button from '@pb/kit-component-button';
import CheckBox from '@pb/kit-component-checkbox';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import SlideBox, {SlideBoxButton} from '@pb/kit-component-slide-box';
import {Textarea} from '@pb/kit-component-textarea-v2';
import Typography from '@pb/kit-component-typography';
import {LocaleContext} from '@pb/kit-context-locale';
import {stringToNumber} from '@pb/kit-formatters';


import {
    apiV2ContractChange,
    apiV2ContractChangeForm,
    apiV2ContractChangeFormContent,
    apiV2ContractChangeHandle,
} from './change-credit-form.module.styl';

import type {OnChangeComment, OnChangeCredits, OnRestoreForm, OnToggleForm, OnUpdateCredits} from './typedef';


type Props = {
    costPerCredit: number | undefined,
    formExpanded: boolean,
    creditsAmount: string,
    remainingCredits: number,
    comment: string,
    onToggleForm: OnToggleForm,
    onChangeCredits: OnChangeCredits,
    onChangeComment: OnChangeComment,
    onRestoreForm: OnRestoreForm,
    onUpdateCredits: OnUpdateCredits,
    disabled?: boolean,
};
export const ChangeCreditForm = (props: Props) => {
    const {
        costPerCredit,
        formExpanded,
        creditsAmount,
        remainingCredits,
        comment,
        onToggleForm,
        onChangeCredits,
        onChangeComment,
        onUpdateCredits,
        onRestoreForm,
        disabled,
    } = props;

    const {locale} = useContext(LocaleContext);

    const validateNumberOfCredits = useCallback(() => {
        if (creditsAmount && (remainingCredits + +creditsAmount) < 0) {
            return {
                code: 'not-enough-credits',
                controlId: 'number-of-credits',
                message: 'You are trying to reduce credits more than current balance',
            };
        }
        return null;
    }, [creditsAmount, remainingCredits]);

    const canApply = useMemo(() => (
        stringToNumber(creditsAmount, locale) !== 0 && !(creditsAmount && (remainingCredits + +creditsAmount) < 0)
    ), [creditsAmount, locale, remainingCredits]);

    return (
        <div className={apiV2ContractChange}>
            <div className={apiV2ContractChangeHandle}>
                <SlideBoxButton onClick={onToggleForm}>
                    {slideBoxClick => (
                        <CheckBox
                            name="default"
                            onCheckBoxChange={slideBoxClick}
                            checked={formExpanded}
                            custom={true}
                            usePropsOnly={true}
                        />
                    )}
                </SlideBoxButton>
            </div>

            <div className={apiV2ContractChangeForm}>
                <Typography fontWeight="semi-bold" variant="heading-level-5">Change Credits</Typography>

                <SlideBox {...{shown: formExpanded}}>
                    <div className={apiV2ContractChangeFormContent}>
                        <Group direction="v">
                            <Group alignItems="start">
                                <Group direction="v" gap="s">
                                    <Item style={{width: 170}}>
                                        <ValidatedControl name="number-of-credits" validate={validateNumberOfCredits}>
                                            {invalid => (
                                                <NumberInput
                                                    width={170}
                                                    inputType={NumberInputTypes.INTEGER}
                                                    valid={invalid ? false : undefined}
                                                    label={<RequiredMark label="Number of Credits"/>}
                                                    maxValue={MAX_CONTRACT_FIELD_VALUE}
                                                    value={creditsAmount ? +creditsAmount : undefined}
                                                    readOnly={disabled}
                                                    displayedValue={creditsAmount ? Integer.format(+creditsAmount, locale) : ''}
                                                    onBlur={value => onChangeCredits(value ? value.toString() : '')}
                                                />
                                            )}
                                        </ValidatedControl>
                                    </Item>
                                    <Item style={{width: 170}}>
                                        <Label>Cost per Credit</Label>
                                        <Input
                                            disabled={true}
                                            value={costPerCredit
                                                ? getCost(costPerCredit, locale, {showSymbol: false})
                                                : '-'}
                                        />
                                    </Item>
                                </Group>
                                <Item grow={1}>
                                    <Item>
                                        <Label>Comment</Label>
                                        <Textarea
                                            {...{
                                                value: comment,
                                                disabled,
                                                resizable: false,
                                                height: 98,
                                                maxLength: 255,
                                                onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                                    onChangeComment(e.target.value),
                                            }}
                                        />
                                    </Item>
                                </Item>
                            </Group>
                            <Group justify="end">
                                <Button
                                    variant="Type.PRIMARY"
                                    disabled={!canApply}
                                    onClick={canApply ? onUpdateCredits : undefined}>
                                    Apply
                                </Button>
                                <Button
                                    variant="Type.SECONDARY"
                                    disabled={!(creditsAmount || comment)}
                                    onClick={onRestoreForm}>
                                    Cancel
                                </Button>
                            </Group>
                        </Group>
                    </div>
                </SlideBox>
            </div>
        </div>
    );
};
